import React, { useEffect, useState } from "react";
import { stat } from "@/lib/i18nFormat";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, LoadingBlock } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CheckCheck, Bell, Send } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

interface Notif { id: string; category: string; title: string; body: string; link?: string | null; read: boolean; created_at?: string; }
type Rule = { in_app: boolean; web_push: boolean; email: boolean };

const CHANNELS: (keyof Rule)[] = ["in_app", "web_push", "email"];
const channelLabel: Record<string, string> = { in_app: "In-app", web_push: "Web push", email: "Email" };

export default function Notifications() {
  const {
    t
  } = useI18n();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<Notif[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [muted, setMuted] = useState<string[]>([]);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [rules, setRules] = useState<Record<string, Rule>>({});
  const [canManage, setCanManage] = useState(false);
  const [savingRules, setSavingRules] = useState(false);

  const loadFeed = () =>
    api.get("/notifications", { params: { limit: 100 } }).then((r) => setItems(r.data.notifications)).catch(() => {});

  const loadAll = async () => {
    setLoading(true);
    try {
      const [feed, prefs, rulesRes] = await Promise.all([
        api.get("/notifications", { params: { limit: 100 } }),
        api.get("/notifications/preferences"),
        api.get("/notifications/rules"),
      ]);
      setItems(feed.data.notifications);
      setCategories(prefs.data.categories);
      setMuted(prefs.data.preferences.muted || []);
      setPushEnabled(!!prefs.data.preferences.web_push_enabled);
      setRules(rulesRes.data.rules);
      setCanManage(!!rulesRes.data.can_manage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const markRead = async (n: Notif) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    if (n.link) navigate(n.link);
  };
  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    toast.success("All notifications marked read");
  };

  const toggleMute = async (cat: string) => {
    const next = muted.includes(cat) ? muted.filter((c) => c !== cat) : [...muted, cat];
    setMuted(next);
    await api.put("/notifications/preferences", { muted: next, web_push_enabled: pushEnabled });
  };
  const togglePush = async (on: boolean) => {
    setPushEnabled(on);
    await api.put("/notifications/preferences", { muted, web_push_enabled: on });
    if (on && "Notification" in window && Notification.permission !== "granted") {
      try { await Notification.requestPermission(); } catch { /* honest: best-effort */ }
    }
  };

  const setRuleChannel = (cat: string, ch: keyof Rule, val: boolean) =>
    setRules((r) => ({ ...r, [cat]: { ...r[cat], [ch]: val } }));
  const saveRules = async () => {
    setSavingRules(true);
    try {
      await api.put("/notifications/rules", { rules });
      toast.success("Delivery rules saved");
    } catch { toast.error("Could not save rules"); }
    finally { setSavingRules(false); }
  };
  const sendTest = async () => {
    try {
      const r = await api.post("/notifications/test");
      const d = r.data.delivery;
      toast.success(d.in_app ? "Test notification created (check the bell)" : "Test dispatched (in-app suppressed by rules/mute)");
      loadFeed();
    } catch { toast.error("Test failed"); }
  };

  if (loading) return <LoadingBlock />;

  return (
    (<div className="space-y-6" data-testid="notifications-page">
      <PageHeader title="Notifications" subtitle="Your alerts, delivery preferences and tenant rules." />
      <Tabs defaultValue="feed">
        <TabsList data-testid="notifications-tabs">
          <TabsTrigger value="feed" data-testid="tab-feed">{t("Feed")}</TabsTrigger>
          <TabsTrigger value="prefs" data-testid="tab-prefs">{t("Preferences")}</TabsTrigger>
          {canManage && <TabsTrigger value="rules" data-testid="tab-rules">{t("Delivery Rules")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="feed" className="mt-4">
          <div className="mb-3 flex justify-end">
            <Button variant="outline" size="sm" onClick={markAll} data-testid="feed-markall">
              <CheckCheck size={15} className="mr-1.5" />{" "}{t("Mark all read")}</Button>
          </div>
          <Card className="divide-y divide-border">
            {items.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-muted-foreground" data-testid="feed-empty">
                <Bell className="mx-auto mb-2 opacity-40" size={22} />{" "}{t("You're all caught up.")}</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => markRead(n)}
                  data-testid="feed-item"
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary ${n.read ? "opacity-60" : ""}`}
                >
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand" style={{ visibility: n.read ? "hidden" : "visible" }} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{n.title}</span>
                      <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{stat(n.category)}</span>
                    </span>
                    <span className="block text-sm text-muted-foreground">{n.body}</span>
                  </span>
                </button>
              ))
            )}
          </Card>
        </TabsContent>

        <TabsContent value="prefs" className="mt-4 space-y-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-foreground">{t("Web push on this device")}</div>
                <div className="text-xs text-muted-foreground">{t("Best-effort browser notifications. In-app alerts remain authoritative.")}</div>
              </div>
              <Switch checked={pushEnabled} onCheckedChange={togglePush} data-testid="pref-push-toggle" />
            </div>
          </Card>
          <Card className="p-4">
            <div className="mb-3 text-sm font-semibold text-foreground">{t("Mute categories")}</div>
            <div className="space-y-2.5">
              {categories.map((cat) => (
                <div key={cat} className="flex items-center justify-between">
                  <span className="text-sm capitalize text-foreground">{cat}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{muted.includes(cat) ? "Muted" : t("On")}</span>
                    <Switch
                      checked={!muted.includes(cat)}
                      onCheckedChange={() => toggleMute(cat)}
                      data-testid={`pref-cat-${cat}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {canManage && (
          <TabsContent value="rules" className="mt-4 space-y-4">
            <Card className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-foreground">{t("Tenant delivery rules")}</div>
                  <div className="text-xs text-muted-foreground">{t("Which channels each category may use. Email is Not Configured (Resend).")}</div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={sendTest} data-testid="rules-test">
                    <Send size={14} className="mr-1.5" />{" "}{t("Send test")}</Button>
                  <Button size="sm" onClick={saveRules} disabled={savingRules} data-testid="rules-save">{t("Save rules")}</Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="py-2 pr-4">{t("Category")}</th>
                      {CHANNELS.map((ch) => <th key={ch} className="py-2 pr-4">{channelLabel[ch]}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(rules).map((cat) => (
                      <tr key={cat} className="border-t border-border">
                        <td className="py-2.5 pr-4 capitalize text-foreground">{cat}</td>
                        {CHANNELS.map((ch) => (
                          <td key={ch} className="py-2.5 pr-4">
                            <Switch
                              checked={!!rules[cat]?.[ch]}
                              onCheckedChange={(v) => setRuleChannel(cat, ch, v)}
                              disabled={ch === "email"}
                              data-testid={`rule-${cat}-${ch}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>)
  );
}
